import React from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ConfirmModal from "../confirm.modal";
import type { getUserForm } from "../../../lib/apis/dtos/request/user.request";
// import { matchUser } from "../../../lib/apis/v1/user.service";
import { getQueryClient } from "../../../config/queryClient.config";
import type { AuthSignUpByLoginIdForm } from "../../../lib/apis/dtos/request/auth.request";
import { authSignUpByLoginId } from "../../../lib/apis/v1/auth.service";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import type { ModalComponentProps } from "../../../types/modal.types";
import { useUserList } from "../../../lib/apis/hooks/user.hook";

const ROLE_MODEL = [
  { label: "파트너", value: "PARTNER" },
  { label: "오너", value: "OWNER" },
  { label: "어드민", value: "ADMIN" }
];

export default function AccountCreateModal({ id, onClose }: ModalComponentProps) {
  const userForm = { page: 0, size: 1000, role: "OWNER" } as getUserForm;

  const [form, setForm] = React.useState({
    loginId: "",
    email: "",
    password: "0000",
    confirmPassword: "0000",
    username: "",
    phone: "",
    ownerId: "",
    role: "OWNER",
    nickname: "",
  });
  const [, setSelectOwnerName] = React.useState<string>("");
  const [authIdError, setAuthIdError] = React.useState(false);
  // const [emailError, setEmailError] = React.useState(false);
  const [phoneError, setPhoneError] = React.useState(false);
  const [helperText, setHelperText] = React.useState("");
  const [phoneHelper, setPhoneHelper] = React.useState("");
  // const [emailHelper, setEmailHelper] = React.useState("");

  const { data, isSuccess } = useQuery({
    ...useUserList(userForm),
    enabled: true,
  });
  const { result } = data || {};
  const content = result?.content || [];

  // const matchMutation = useMutation({
  //   mutationFn: (form: matchUserForm) => matchUser(form),
  //   onSuccess: (res) => {
  //     const { error: matchError } = res || {};
  //     if (matchError) {
  //       FailSnackbar("계성 생성 실패 (매칭 인원 오류)");
  //     } else {
  //       getQueryClient().invalidateQueries({ queryKey: ["user-list"] });
  //       SuccessSnackbar("계정 생성 완료");
  //       close();
  //     }
  //   },
  // });


  const signupMutation = useMutation({
    mutationFn: (form: AuthSignUpByLoginIdForm) => authSignUpByLoginId(form),
    onSuccess: (data) => {
      const {
        code,
        result: _responseResult,
        message,
        error
      } = data || {};
      if (code === 10400) {
        setHelperText("중복되는 아이디가 있어요");
        setAuthIdError(true);
        FailSnackbar(`계정 생성 실패(${message})`);
      } else if (code === 11111) {
        setPhoneHelper("연락처를 숫자만 11~12자리로 입력해주세요.")
        setPhoneError(true);
        FailSnackbar(`계정 생성 실패(${message})`);
      } else if (error) {
        FailSnackbar("계정 생성 실패");
      } else {
        getQueryClient().invalidateQueries({ queryKey: ["user-list"] });
        SuccessSnackbar("계정 생성 완료");
        onClose?.();
      }
    },
  });

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    if (id == "phone") {
      const numbersOnly = value.replace(/[^0-9]/g, "");
      let formattedNumber = "";

      if (numbersOnly.length <= 3) {
        formattedNumber = numbersOnly;
      } else if (numbersOnly.length <= 7) {
        formattedNumber = numbersOnly.replace(/^(\d{3})(\d{1,4})$/, "$1-$2");
      } else {
        formattedNumber = numbersOnly.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, "$1-$2-$3");
      }

      setForm((state) => ({ ...state, [id]: formattedNumber }));
    } else {
      setForm((state) => ({ ...state, [id]: value }));
    }
  };

  // const validateEmail = (value: string) => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (value === "") {
  //     setEmailError(false);
  //     setHelperText("");
  //   } else if (!emailRegex.test(value)) {
  //     setEmailError(true);
  //     setEmailHelper("아이디 형식을 맞춰주세요. (ex. abcd@abcd.com)");
  //   } else {
  //     setEmailError(false);
  //     setHelperText("");
  //   }
  // };

  const disableCheck = () => {
    if (form.role === "PARTNER") {
      return !form.loginId || !form.username || !form.phone || !form.ownerId;
    } else {
      return !form.loginId || !form.username || !form.phone;
    }
  }

  return (
    <ConfirmModal
      title="직원 계정 생성"
      onClose={onClose}
      rightButton={{
        title: "계정 생성",
        variant: disableCheck() ? "disabled" : "primary",
        disabled: disableCheck(),
        onClick: () => {
          signupMutation.mutate(form);
        },
      }}
      id={id}
    >
      <p className="text-[16px] py-4 text-gray-60">생성할 계정의 정보를 입력해주세요.</p>

      <div className="flex flex-col gap-4 mt-4">
        <p className="text-[16px] mt-[16px] ">계정 구분</p>
        <RadioGroup
          className="px-[9px]"
          row
          aria-labelledby="demo-radio-bujttons-group-label"
          name="radio-buttons-group"
        >
          {ROLE_MODEL.map(({ label, value }, index) => (
            <FormControlLabel
              key={index}
              value={value}
              control={
                <Radio
                  checked={form.role === value}
                  onClick={() => {
                    setForm((state) => ({ ...state, role: value, ownerId: "" }))
                    setSelectOwnerName("")
                  }}
                />
              }
              label={label}
            />
          ))}
        </RadioGroup>
        <TextField
          id="username"
          className="w-[552px] h-[56px]"
          label="이름*"
          placeholder="이름 입력 (ex. 김오너)"
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={changeHandler}
        />
        <TextField
          id="loginId"
          className="w-[552px] h-[56px]"
          label="아이디*"
          placeholder="아이디 입력 (ex. abcd)"
          error={authIdError}
          helperText={helperText}
          value={form.loginId}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            changeHandler(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
        {/* <p className="text-[#007AFF] text-[16px]">
          * 비밀번호는 0000으로 자동 부여됩니다. 마이페이지에서 본인이
          수정가능합니다.
        </p> */}
        <TextField
          id="phone"
          className="w-[552px] h-[56px]"
          label="연락처*"
          type="tel"
          error={phoneError}
          helperText={phoneHelper}
          placeholder="연락처 입력 (ex. 010-1234-5678)"
          value={form.phone}
          inputProps={{ maxLength: 13 }}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={changeHandler}
        />
        {form.role === "PARTNER" && (
          <Autocomplete
            disablePortal
            id="owner-autocomplete"
            className="w-[552px]"
            options={content || []}
            loading={!isSuccess}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.username || "";
            }}
            value={content?.find((item) => item.loginId?.toString() === form.ownerId)?.username || null}
            slotProps={{
              popper: {
                sx: { zIndex: 3000 }
              },
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === "string") return;
              if (newValue) {
                setForm((state) => ({ ...state, ownerId: newValue?.loginId?.toString() || "" }));
                setSelectOwnerName(newValue?.username || "");
              } else {
                setForm((state) => ({ ...state, ownerId: "" }));
                setSelectOwnerName("");
              }
            }}
            freeSolo
            renderValue={(value, getItemProps) => (
              <Chip label={value.toString()} {...getItemProps()} />
            )}
            // 5. 검색창 스타일 및 플레이스홀더
            renderInput={(params) => (
              <TextField
                {...params}
                label="오너 배정"
                placeholder="오너명으로 검색"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!isSuccess ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.loginId}>
                {option.username}
              </li>
            )}
          />
        )}
      </div>
    </ConfirmModal>
  );
};
